import { Controller } from "@hotwired/stimulus";

declare const MercadoPago: any;

type MercadoPagoSecureField = {
  unmount?: () => void;
  mount?: (containerId: string) => void;
  on?: (event: string, cb: (data: unknown) => void) => void;
};

export default class extends Controller {
  static targets = [
    "form",
    "cardToken",
    "paymentMethodId",
    "submitButton",
    "errorContainer",
    "cardholderName",
    "identificationType",
    "identificationNumber"
  ];
  declare readonly hasErrorContainerTarget: boolean;
  static values = { publicKey: String, amount: String };

  declare readonly formTarget: HTMLFormElement;
  declare readonly cardTokenTarget: HTMLInputElement;
  declare readonly paymentMethodIdTarget: HTMLInputElement;
  declare readonly submitButtonTarget: HTMLButtonElement;
  declare readonly errorContainerTarget: HTMLElement;
  declare readonly cardholderNameTarget: HTMLInputElement;
  declare readonly identificationTypeTarget: HTMLSelectElement;
  declare readonly identificationNumberTarget: HTMLInputElement;
  declare readonly publicKeyValue: string;
  declare readonly amountValue: string;

  private mp: any;
  private cardNumberField: MercadoPagoSecureField | undefined;
  private expirationDateField: MercadoPagoSecureField | undefined;
  private securityCodeField: MercadoPagoSecureField | undefined;
  private paymentMethodId: string | null = null;
  private sdkRetryTimer: number | null = null;
  private formSubmitHandler?: (e: Event) => void;
  private fieldsMounted = false;
  private t(key: string, defaultValue: string) {
    const I18n = (window as any).I18n;
    if (I18n?.t) {
      try {
        return I18n.t(key, { defaultValue }) || defaultValue;
      } catch {
        return defaultValue;
      }
    }
    return defaultValue;
  }

  connect() {
    this.initializeMercadoPago();
    // Attach a resilient submit handler in case onSubmit from cardForm isn't available
    this.formSubmitHandler = (e: Event) => this.handleManualSubmit(e);
    this.formTarget.addEventListener("submit", this.formSubmitHandler);
  }

  private initializeMercadoPago() {
    if (!this.publicKeyValue) {
      this.showError(this.t("pacts.payment.errors.missing_public_key", "Missing Mercado Pago public key"));
      return;
    }
    if (!this.amountValue) {
      this.showError(this.t("pacts.payment.errors.missing_amount", "Payment amount is missing"));
      return;
    }
    if (typeof MercadoPago === "undefined") {
      console.warn("MercadoPago SDK not loaded yet, will retry...");
      if (this.sdkRetryTimer == null) {
        this.sdkRetryTimer = window.setTimeout(() => {
          this.sdkRetryTimer = null;
          this.initializeMercadoPago();
        }, 500);
      }
      return;
    }
    if (!this.mp) {
      try {
        this.mp = new MercadoPago(this.publicKeyValue);
      } catch (e) {
        console.error("MercadoPago init error", e);
        this.showError(this.t("pacts.payment.errors.sdk_init_failed", "Failed to initialize Mercado Pago SDK."));
        return;
      }
    }
    this.mountSecureFields();
    this.populateIdentificationTypes();
  }

  disconnect() {
    // Remove manual submit handler
    if (this.formSubmitHandler) {
      this.formTarget.removeEventListener("submit", this.formSubmitHandler);
      this.formSubmitHandler = undefined;
    }

    // Best-effort cleanup to avoid "context already exists" across Turbo navigations
    try {
      this.cardNumberField?.unmount?.();
      this.expirationDateField?.unmount?.();
      this.securityCodeField?.unmount?.();
    } catch (e) {
      console.warn("Failed to unmount MercadoPago fields during disconnect.", e);
    } finally {
      this.cardNumberField = undefined;
      this.expirationDateField = undefined;
      this.securityCodeField = undefined;
      this.fieldsMounted = false;
      this.paymentMethodId = null;
      delete (window as any).__pacta_mp_fields;
    }
  }

  private mountSecureFields() {
    // Ensure form has an id (used when we submit programmatically)
    const formId = this.formTarget.id || "form-checkout";
    if (!this.formTarget.id) this.formTarget.id = formId;

    try {
      const existing = (window as any).__pacta_mp_fields;
      if (existing?.mounted) {
        this.fieldsMounted = true;
        this.cardNumberField = existing.cardNumberField;
        this.expirationDateField = existing.expirationDateField;
        this.securityCodeField = existing.securityCodeField;
        this.paymentMethodId = existing.paymentMethodId || null;
        return;
      }

      const requiredIds = ["form-checkout__cardNumber", "form-checkout__expirationDate", "form-checkout__securityCode"];
      const missing = requiredIds.filter((id) => !document.getElementById(id));
      if (missing.length > 0) {
        console.error("Missing secure field containers", missing);
        this.showError(this.t("pacts.payment.errors.missing_secure_containers", "Secure field containers not found."));
        return;
      }

      this.cardNumberField = this.mp.fields
        .create("cardNumber", { placeholder: "1234 5678 9012 3456" })
        .mount("form-checkout__cardNumber");

      this.expirationDateField = this.mp.fields
        .create("expirationDate", { placeholder: "MM/YY" })
        .mount("form-checkout__expirationDate");

      this.securityCodeField = this.mp.fields
        .create("securityCode", { placeholder: "CVV" })
        .mount("form-checkout__securityCode");

      // Capture BIN changes to infer payment method id
      this.cardNumberField?.on?.("binChange", (data: any) => {
        const bin = data?.bin;
        if (typeof bin === "string" && bin.length >= 6) {
          void this.resolvePaymentMethodId(bin);
        }
      });

      (window as any).__pacta_mp_fields = {
        mounted: true,
        cardNumberField: this.cardNumberField,
        expirationDateField: this.expirationDateField,
        securityCodeField: this.securityCodeField,
        paymentMethodId: this.paymentMethodId
      };
      this.fieldsMounted = true;
    } catch (e) {
      console.error("Failed to initialize MercadoPago Secure Fields", e);
      this.showError(this.t("pacts.payment.errors.form_init_failed", "Could not initialize payment form."));
    }
  }

  private async populateIdentificationTypes() {
    const select = document.getElementById("form-checkout__identificationType") as HTMLSelectElement | null;
    if (!select) return;
    if (select.options.length > 0) return;

    try {
      const identificationTypes = await this.mp.getIdentificationTypes();
      if (!Array.isArray(identificationTypes)) return;

      select.options.length = 0;
      identificationTypes.forEach((t: any) => {
        const opt = document.createElement("option");
        opt.value = t?.id;
        opt.textContent = t?.name;
        select.appendChild(opt);
      });
      if (select.options.length > 0) {
        select.value = select.options[0].value;
      }
    } catch (e) {
      console.warn("Error getting identificationTypes", e);
    }
  }

  private async resolvePaymentMethodId(bin: string) {
    try {
      const response = await this.mp.getPaymentMethods({ bin });
      const id = response?.results?.[0]?.id;
      if (typeof id === "string" && id.length > 0) {
        this.paymentMethodId = id;
        if ((window as any).__pacta_mp_fields) {
          (window as any).__pacta_mp_fields.paymentMethodId = id;
        }
      }
    } catch (e) {
      console.warn("Error getting payment methods", e);
    }
  }

  private showError(message: string) {
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.textContent = message;
      this.errorContainerTarget.classList.remove("hidden");
      this.errorContainerTarget.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      alert(message);
    }
  }

  private clearErrors() {
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.textContent = "";
      this.errorContainerTarget.classList.add("hidden");
    }
  }

  private setLoading(loading: boolean) {
    this.submitButtonTarget.disabled = loading;
    this.submitButtonTarget.classList.toggle("opacity-50", loading);
    this.submitButtonTarget.classList.toggle("cursor-not-allowed", loading);
  }
  private async handleManualSubmit(event: Event) {
    event.preventDefault();
    this.clearErrors();
    this.setLoading(true);

    try {
      if (!this.fieldsMounted) {
        throw Object.assign(new Error("Secure Fields not mounted"), { cause: [{ description: "Secure Fields not mounted" }] });
      }

      const cardholderName = this.cardholderNameTarget.value;
      const identificationType = this.identificationTypeTarget.value;
      const identificationNumber = this.identificationNumberTarget.value;

      if (!cardholderName) {
        throw Object.assign(new Error(this.t("pacts.payment.errors.missing_cardholder_name", "Cardholder name is required")), {
          cause: [{ description: this.t("pacts.payment.errors.missing_cardholder_name", "Cardholder name is required") }]
        });
      }

      const tokenParams: any = { cardholderName };
      if (identificationType && identificationNumber) {
        tokenParams.identificationType = identificationType;
        tokenParams.identificationNumber = identificationNumber;
      }

      // createCardToken returns a Promise in SDK V2
      const token = await this.mp.fields.createCardToken(tokenParams);
      if (!token?.id) {
        throw Object.assign(new Error(this.t("pacts.payment.errors.token_not_created", "Token not created")), {
          cause: [{ description: this.t("pacts.payment.errors.token_not_created", "Token not created") }]
        });
      }

      this.cardTokenTarget.value = token.id;
      if (this.paymentMethodIdTarget) {
        this.paymentMethodIdTarget.value = this.paymentMethodId || "";
      }
      this.formTarget.submit();
    } catch (error: any) {
      console.error("Error creating token:", error);
      const localizedFallback = this.t(
        "pacts.payment.errors.token_error",
        "An error occurred processing the card. Please check the details."
      );
      const message = error.cause?.[0]?.description || localizedFallback;
      this.showError(message);
    } finally {
      this.setLoading(false);
    }
  }
}
